import { LEASE_OPTIONS, useLeaseCompanyOptions } from "modules/LeadsDashboard/hooks";
import { setLeadsNotSnoozedFilters, useLeadsNotSnoozedFilters } from "modules/LeadsDashboard/Leads/NotSnoozedLeads/hooks";
import { QUESTION_STATUSES_DEFAULT_STATUSES } from "modules/LeadsDashboard/Leads/SnoozedLeads/hooks";
import { LEADS_NOT_SNOOZED_STORAGE_KEYS } from "modules/LeadsDashboard/storageKeys";
import { setPreference } from "utils/common";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { PAGE_SIZE } from "utils/common";

export const useNotSnoozedLeadsTableFilterHandlers = () => {
  const { leaseCompanyOptions } = useLeaseCompanyOptions();

  const notSnoozedFilters = useLeadsNotSnoozedFilters();
  const { page: currentPage, page_size: pageSize, questions, statuses } = notSnoozedFilters;

  const onPageChange = (data: { activePage: number }) => setLeadsNotSnoozedFilters({ ...notSnoozedFilters, page: data.activePage });
  const handlePaginationSelect: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater({ pageIndex: currentPage - 1, pageSize }) : updater;
    onPageChange({ activePage: newState.pageIndex + 1 });
  };

  const handlePageSizeChange = (value: PAGE_SIZE) => {
    setPreference(LEADS_NOT_SNOOZED_STORAGE_KEYS.PageSizePreference, value);
    setLeadsNotSnoozedFilters({ ...notSnoozedFilters, page_size: value, page: 1 });
  };

  const handleQuestionResultStatusSelect = (questionStatuses: string[]) => {
    const isCurrentlyAll = !statuses || statuses.length === QUESTION_STATUSES_DEFAULT_STATUSES.length;
    const selectedStatuses = questionStatuses.map(Number).filter(Boolean);

    const isSelectingAll = questionStatuses.includes("all") && !isCurrentlyAll;
    const isNoneOrEverySelected = !selectedStatuses.length || selectedStatuses.length === QUESTION_STATUSES_DEFAULT_STATUSES.length;

    const newStatuses = isSelectingAll || isNoneOrEverySelected ? null : selectedStatuses;
    setPreference(LEADS_NOT_SNOOZED_STORAGE_KEYS.QuestionStatusPreference, newStatuses);
    setLeadsNotSnoozedFilters({ ...notSnoozedFilters, statuses: newStatuses, page: 1 });
  };

  const handleOnLeaseSelect = (leaseCompany: string[]) => {
    const selectedLeaseCompanyValue = leaseCompanyOptions.find(option => option.value === leaseCompany[0]) || null;

    let is_lease_company: boolean | null = null;
    if (selectedLeaseCompanyValue && selectedLeaseCompanyValue.value !== LEASE_OPTIONS.All) is_lease_company = selectedLeaseCompanyValue.value === LEASE_OPTIONS.Lease;

    setPreference(LEADS_NOT_SNOOZED_STORAGE_KEYS.RegVinPreference, is_lease_company);
    setLeadsNotSnoozedFilters({
      ...notSnoozedFilters,
      is_lease_company,
      page: 1
    });
  };

  const handleOnQuestionSelect = (value: string[]) => {
    const isResetNotSelected = value.includes("all") && questions !== null;
    const newQuestions = isResetNotSelected ? null : value.filter(el => el !== "all").map(el => Number(el.split("-")[1]));

    setPreference(LEADS_NOT_SNOOZED_STORAGE_KEYS.InterventionQuestionResultPreference, newQuestions);
    setLeadsNotSnoozedFilters({ ...notSnoozedFilters, questions: newQuestions, page: 1 });
  };

  const handleDateRangeChange = ([start, end]: [Date | null, Date | null]) => {
    if (start && end) setLeadsNotSnoozedFilters({ ...notSnoozedFilters, date_from: start, date_to: end, page: 1 });
  };

  return { handleOnLeaseSelect, handleDateRangeChange, handlePaginationSelect, handleOnQuestionSelect, handleQuestionResultStatusSelect, handlePageSizeChange };
};
